// Cards (E01-S4-T4; D-06, contracts 1.0 section 7). What the surface shows beside the conversation.
// No card has a field for a raw screen value: a FieldCard states how the field was treated — the
// server says it, the client does not guess — and never what it held.
import { z } from "zod";
import { Citation } from "../citation.js";
import { Count, Id, NonEmpty, strictObject } from "../primitives.js";
import { NOTE_MAX_LENGTH } from "../pack.js";
import { SopStepRef } from "../tripwire.js";
const Seconds = z.number().nonnegative();
export const ClipCard = strictObject({
    kind: z.literal("clip"),
    beatId: Id,
    titleId: Id,
    title: NonEmpty,
    start: Seconds,
    end: Seconds,
    mediaUrl: z.url({ protocol: /^https?$/ }), // a ten-minute address from GET /v1/media/url (D-84)
    citation: Citation,
}).refine((c) => c.start < c.end, { message: "a clip starts before it ends", path: ["end"] });
export const FieldTreatment = z.enum(["read", "banded", "tokenised", "masked", "not-read"]);
export const FieldCard = strictObject({
    kind: z.literal("field"),
    fieldId: Id,
    label: NonEmpty,
    guide: NonEmpty,
    plain: NonEmpty.optional(),
    citation: Citation,
    treatment: FieldTreatment,
});
/** A span of the draft the user still has to fill — a missing reason, say. */
export const NoteBlank = strictObject({ start: Count, end: Count, hint: NonEmpty });
export const NoteDraftCard = strictObject({
    kind: z.literal("note-draft"),
    // Not trimmed: the draft is shown and copied exactly, and the blanks index into it.
    text: z.string().min(1).max(NOTE_MAX_LENGTH),
    maxLength: z.number().int().positive().max(NOTE_MAX_LENGTH),
    blanks: z.array(NoteBlank),
}).superRefine((n, ctx) => {
    if (n.text.length > n.maxLength)
        ctx.addIssue({
            code: "custom",
            message: `the draft is longer than its maxLength (${String(n.maxLength)})`,
            path: ["text"],
        });
    let after = 0;
    n.blanks.forEach((b, i) => {
        if (!(b.start < b.end))
            ctx.addIssue({
                code: "custom",
                message: "a blank covers at least one character",
                path: ["blanks", i, "end"],
            });
        if (b.end > n.text.length)
            ctx.addIssue({
                code: "custom",
                message: "a blank lies within the draft's text",
                path: ["blanks", i, "end"],
            });
        if (b.start < after)
            ctx.addIssue({
                code: "custom",
                message: "blanks are in order and do not overlap",
                path: ["blanks", i, "start"],
            });
        after = Math.max(after, b.end);
    });
});
export const TitleCard = strictObject({
    kind: z.literal("title"),
    titleId: Id,
    title: NonEmpty,
    runtimeSeconds: z.number().int().positive(),
    reason: NonEmpty,
});
export const EscalationCard = strictObject({
    kind: z.literal("escalation"),
    to: NonEmpty,
    text: NonEmpty,
    sopStep: SopStepRef,
    citation: Citation.optional(),
});
export const Card = z.discriminatedUnion("kind", [
    ClipCard,
    FieldCard,
    NoteDraftCard,
    TitleCard,
    EscalationCard,
]);
//# sourceMappingURL=cards.js.map